// Recipes tab of the admin page: every recipe in one table. Each row lets you
// rename the recipe (Enter or blur saves), pick a new owner, or jump to the
// full edit page. Checked rows can be reassigned or deleted in bulk.

import React, { useEffect, useRef, useState } from "react";
import { Table, Form, Button, Spinner, Row, Col } from "react-bootstrap";
import { common } from "../../js/common";
import { MAX_TITLE_LENGTH } from "../subComponents/submitRecipe/TitleInput";

interface AdminRecipe {
  id: number;
  name: string;
  creatorId: number;
  creatorName?: string;
  createdOn: string;
}

interface Account {
  id: number;
  userName: string;
}

/** Runs one request per id; returns the ids that failed. */
async function forEachId(ids: number[], fn: (id: number) => Promise<Response>): Promise<number[]> {
  const results = await Promise.allSettled(ids.map((id) => fn(id).then((res) => (res.ok ? id : Promise.reject(id)))));
  return ids.filter((_, i) => results[i].status === "rejected");
}

function RecipesTab() {
  const [recipes, setRecipes] = useState<AdminRecipe[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [titleDrafts, setTitleDrafts] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkOwner, setBulkOwner] = useState("");
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const [recipesRes, accountsRes] = await Promise.all([
          common.fetchRestAPI("recipes", "GET"),
          common.fetchRestAPI("accounts", "GET"),
        ]);
        if (!recipesRes.ok) throw new Error("Failed to load recipes");
        if (!accountsRes.ok) throw new Error("Failed to load accounts");
        const recipesBody = await recipesRes.json();
        const accountsBody = await accountsRes.json();
        setRecipes(recipesBody.recipes ?? []);
        setAccounts(accountsBody.accounts ?? []);
      } catch (err: any) {
        setError(err.message || "Failed to load recipes");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allSelected = recipes.length > 0 && selected.size === recipes.length;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selected.size > 0 && !allSelected;
  }, [selected, allSelected]);

  const setBusy = (ids: number[], busy: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (busy ? next.add(id) : next.delete(id)));
      return next;
    });

  const ownerName = (creatorId: number) => accounts.find((a) => a.id === creatorId)?.userName ?? `account ${creatorId}`;

  const patchRecipe = (id: number, body: object) => common.fetchRestAPI(`recipes/${id}`, "PATCH", body);

  const saveTitle = async (recipe: AdminRecipe) => {
    const draft = titleDrafts[recipe.id];
    if (draft === undefined) return;
    const name = draft.trim();
    setTitleDrafts((prev) => {
      const rest = { ...prev };
      delete rest[recipe.id];
      return rest;
    });
    if (!name || name === recipe.name) return;
    setError("");
    setNotice("");
    setBusy([recipe.id], true);
    try {
      const res = await patchRecipe(recipe.id, { name });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ? `Failed to rename "${recipe.name}": ${body.error}` : `Failed to rename "${recipe.name}"`);
      }
      setRecipes((prev) => prev.map((r) => (r.id === recipe.id ? { ...r, name } : r)));
      setNotice(`Renamed "${recipe.name}" to "${name}".`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy([recipe.id], false);
    }
  };

  const changeOwner = async (recipe: AdminRecipe, creatorId: number) => {
    if (creatorId === recipe.creatorId) return;
    setError("");
    setNotice("");
    setBusy([recipe.id], true);
    try {
      const res = await patchRecipe(recipe.id, { creatorId });
      if (!res.ok) throw new Error(`Failed to change the owner of "${recipe.name}"`);
      setRecipes((prev) =>
        prev.map((r) => (r.id === recipe.id ? { ...r, creatorId, creatorName: ownerName(creatorId) } : r)),
      );
      setNotice(`"${recipe.name}" is now owned by ${ownerName(creatorId)}.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy([recipe.id], false);
    }
  };

  const applyBulkOwner = async () => {
    const creatorId = Number(bulkOwner);
    const ids = Array.from(selected);
    if (!creatorId || ids.length === 0) return;
    setError("");
    setNotice("");
    setBusy(ids, true);
    try {
      const failed = await forEachId(ids, (id) => patchRecipe(id, { creatorId }));
      const changed = ids.filter((id) => !failed.includes(id));
      setRecipes((prev) =>
        prev.map((r) => (changed.includes(r.id) ? { ...r, creatorId, creatorName: ownerName(creatorId) } : r)),
      );
      setNotice(`${changed.length} recipe${changed.length === 1 ? "" : "s"} now owned by ${ownerName(creatorId)}.`);
      if (failed.length) setError(`Failed to change the owner of ${failed.length} recipe${failed.length === 1 ? "" : "s"}.`);
      setSelected(new Set(failed));
      setBulkOwner("");
    } finally {
      setBusy(ids, false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} recipe${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setError("");
    setNotice("");
    setBusy(ids, true);
    try {
      const failed = await forEachId(ids, (id) => common.fetchRestAPI(`recipes/${id}`, "DELETE"));
      const deleted = ids.filter((id) => !failed.includes(id));
      setRecipes((prev) => prev.filter((r) => !deleted.includes(r.id)));
      setNotice(`Deleted ${deleted.length} recipe${deleted.length === 1 ? "" : "s"}.`);
      if (failed.length) setError(`Failed to delete ${failed.length} recipe${failed.length === 1 ? "" : "s"}.`);
      setSelected(new Set(failed));
    } finally {
      setBusy(ids, false);
    }
  };

  const toggle = (id: number, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" size="sm" role="status" />
      </div>
    );
  }

  const anySelected = selected.size > 0;
  const anyBusy = busyIds.size > 0;

  return (
    <div>
      <h2 className="h4">Recipes</h2>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError("")} aria-label="Close"></button>
        </div>
      )}
      {notice && (
        <div className="alert alert-success" role="alert">
          {notice}
        </div>
      )}

      <Row className="align-items-center g-2 mb-3" data-testid="bulk-actions">
        <Col xs="auto" className="text-muted">
          {selected.size} selected
        </Col>
        <Col xs="auto">
          <Form.Select
            size="sm"
            value={bulkOwner}
            disabled={!anySelected || anyBusy}
            aria-label="New owner for selected recipes"
            onChange={(e) => setBulkOwner(e.target.value)}
          >
            <option value="">Change owner to…</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.userName}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="primary" disabled={!anySelected || !bulkOwner || anyBusy} onClick={applyBulkOwner}>
            Apply owner
          </Button>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="outline-danger" disabled={!anySelected || anyBusy} onClick={deleteSelected}>
            Delete selected
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                ref={selectAllRef}
                aria-label="Select all recipes"
                checked={allSelected}
                disabled={recipes.length === 0}
                onChange={(e) => setSelected(e.target.checked ? new Set(recipes.map((r) => r.id)) : new Set())}
              />
            </th>
            <th>ID</th>
            <th>Title</th>
            <th>Owner</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {recipes.map((recipe) => {
            const busy = busyIds.has(recipe.id);
            const hasOwnerOption = accounts.some((a) => a.id === recipe.creatorId);
            return (
              <tr key={recipe.id} data-testid="admin-recipe-row">
                <td>
                  <Form.Check
                    type="checkbox"
                    aria-label={`Select ${recipe.name}`}
                    checked={selected.has(recipe.id)}
                    disabled={busy}
                    onChange={(e) => toggle(recipe.id, e.target.checked)}
                  />
                </td>
                <td>{recipe.id}</td>
                <td>
                  <Form.Control
                    size="sm"
                    value={titleDrafts[recipe.id] ?? recipe.name}
                    maxLength={MAX_TITLE_LENGTH}
                    disabled={busy}
                    aria-label={`Title for recipe ${recipe.id}`}
                    onChange={(e) => setTitleDrafts((prev) => ({ ...prev, [recipe.id]: e.target.value }))}
                    onBlur={() => saveTitle(recipe)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={recipe.creatorId}
                    disabled={busy}
                    aria-label={`Owner for recipe ${recipe.id}`}
                    onChange={(e) => changeOwner(recipe, Number(e.target.value))}
                  >
                    {!hasOwnerOption && (
                      <option value={recipe.creatorId}>{recipe.creatorName || `account ${recipe.creatorId}`}</option>
                    )}
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.userName}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>{new Date(recipe.createdOn).toLocaleDateString()}</td>
                <td className="text-end">
                  <Button variant="outline-primary" size="sm" href={`/submit?edit=${recipe.id}`} disabled={busy}>
                    Edit
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {recipes.length === 0 && <p>No recipes found.</p>}
    </div>
  );
}

export default RecipesTab;
